import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, Heart, Phone, Mail, MapPin, Star, CheckCircle, Users, Flower, TrendingUp, MessageCircle } from 'lucide-react';
import MultiWebsiteLogin from './components/MultiWebsiteLogin';
import { createApiClient } from './utils/multiWebsiteApi';

const NehaPatelWebsite = () => {
  const [currentPage, setCurrentPage] = useState('home');

  // Check for admin route
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/admin') {
      setCurrentPage('admin');
    }
  }, []);

  // Available programs for Neha Patel
  const [courses] = useState([
    {
      id: 1,
      title: "Happiness Program",
      course_type: "Happiness Program",
      duration: "3 days",
      schedule: "Weekend batches available",
      description: "Learn powerful breathing techniques and meditation to reduce stress, increase energy levels, and bring a deep sense of peace and happiness.",
      from_date: "2025-09-15",
      to_date: "2025-09-17",
      timing: "9:00 AM - 6:00 PM",
      venue: "Art of Living Center, Mumbai",
      is_online: false,
      contact_numbers: "9876543210",
      registration_link: "https://wa.me/919876543210?text=I%20want%20to%20register%20for%20Happiness%20Program",
      is_active: true
    },
    {
      id: 2,
      title: "Online Meditation & Breath Workshop",
      course_type: "Online Meditation & Breath Workshop",
      duration: "1.5 hours",
      schedule: "Evening sessions",
      description: "Join from anywhere! Learn basic breathing techniques and meditation practices for daily stress management.",
      from_date: "2025-09-10",
      to_date: null,
      timing: "7:00 PM - 8:30 PM",
      venue: null,
      is_online: true,
      contact_numbers: "9876543210",
      registration_link: "https://wa.me/919876543210?text=I%20want%20to%20register%20for%20Online%20Meditation%20Workshop",
      is_active: true
    }
  ]);

  // Upcoming Programs Component (from rimaengineer reference)
  const UpcomingPrograms = ({ programs }) => {
    const parseContactNumbers = (contactStr) => {
      if (!contactStr) return [];
      return contactStr.split(',').map(num => num.trim()).filter(Boolean);
    };

    const formatDateRange = (program) => {
      if (!program.from_date) return '';
      
      const fromDate = new Date(program.from_date);
      const toDate = program.to_date ? new Date(program.to_date) : null;
      
      if (!toDate || fromDate.toDateString() === toDate.toDateString()) {
        return fromDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        });
      }
      
      const isSameMonth = fromDate.getMonth() === toDate.getMonth() && fromDate.getFullYear() === toDate.getFullYear();
      
      if (isSameMonth) {
        return `${fromDate.getDate()} - ${toDate.getDate()} ${fromDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`;
      } else {
        return `${fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
      }
    };

    const formatPhoneNumber = (phoneNumber) => {
      if (!phoneNumber) return '';
      const digits = phoneNumber.replace(/\D/g, '');
      if (digits.length === 10) {
        return `+91 ${digits.substring(0, 5)} ${digits.substring(5)}`;
      }
      return phoneNumber;
    };

    const createTelUrl = (phoneNumber) => {
      if (!phoneNumber) return '';
      const digits = phoneNumber.replace(/\D/g, '');
      if (digits.length === 10) {
        return `tel:+91${digits}`;
      }
      return `tel:${digits}`;
    };

    const getColorScheme = (courseType) => {
      const colorSchemes = {
        'Happiness Program': {
          gradient: 'bg-gradient-to-r from-orange-500 to-red-500',
          button: 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600',
          badge: 'bg-orange-100 text-orange-700'
        },
        'Online Meditation & Breath Workshop': {
          gradient: 'bg-gradient-to-r from-cyan-500 to-blue-500',
          button: 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600',
          badge: 'bg-cyan-100 text-cyan-700'
        }
      };
      
      return colorSchemes[courseType] || {
        gradient: 'bg-gradient-to-r from-orange-500 to-orange-600',
        button: 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700',
        badge: 'bg-orange-100 text-orange-700'
      };
    };

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Upcoming <span className="text-teal-600">Programs</span>
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Join our transformative Art of Living programs designed to bring peace, happiness, and wellness into your life. 
              Each program offers powerful techniques for stress reduction, mental clarity, and spiritual growth.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2 max-w-5xl mx-auto">
            {programs.filter(program => program.is_active).map((program) => {
              const contactNumbers = parseContactNumbers(program.contact_numbers);
              const colorScheme = getColorScheme(program.course_type);

              return (
                <div key={program.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden border border-gray-100">
                  {/* Header */}
                  <div className={`${colorScheme.gradient} px-6 py-4`}>
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-white font-semibold text-lg leading-tight flex-1 pr-3">
                        {program.title}
                      </h3>
                      <span className={`${colorScheme.badge} text-xs px-3 py-1 rounded-full flex-shrink-0 whitespace-nowrap font-medium`}>
                        {program.course_type.split(' ')[0]}
                      </span>
                    </div>
                    {program.description && (
                      <p className="text-white text-opacity-95 text-sm leading-relaxed">
                        {program.description}
                      </p>
                    )}
                  </div>
                  
                  <div className="p-6">
                    {/* Program Details */}
                    <div className="space-y-4 mb-6">
                      {/* Date */}
                      {program.from_date && (
                        <div className="flex items-center gap-3">
                          <Calendar className="h-5 w-5 text-teal-600 flex-shrink-0" />
                          <span className="font-semibold text-gray-900 text-sm">
                            {formatDateRange(program)}
                          </span>
                        </div>
                      )}

                      {/* Timing */}
                      {program.timing && (
                        <div className="flex items-center gap-3">
                          <svg className="h-5 w-5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700 text-sm">
                            {program.timing}
                          </span>
                        </div>
                      )}

                      {/* Duration */}
                      {program.duration && (
                        <div className="flex items-center gap-3">
                          <svg className="h-5 w-5 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700 text-sm">
                            {program.duration}
                          </span>
                        </div>
                      )}

                      {/* Location */}
                      <div className="flex items-start gap-3">
                        {program.is_online ? (
                          <>
                            <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <div className="flex items-center gap-2">
                              <span className="text-gray-700 text-sm">Online</span>
                              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
                                Virtual
                              </span>
                            </div>
                          </>
                        ) : program.venue ? (
                          <>
                            <MapPin className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 leading-tight text-sm">
                              {program.venue}
                            </span>
                          </>
                        ) : (
                          <>
                            <MapPin className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-400 text-sm">Location TBD</span>
                          </>
                        )}
                      </div>

                      {/* Contact Numbers */}
                      {contactNumbers.length > 0 && (
                        <div className="flex items-start gap-3">
                          <Phone className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                          <div className="flex flex-wrap gap-1">
                            {contactNumbers.map((number, idx) => (
                              <a 
                                key={idx}
                                href={createTelUrl(number)}
                                className="text-blue-600 hover:text-blue-800 hover:underline font-semibold text-sm"
                              >
                                {formatPhoneNumber(number)}
                                {idx < contactNumbers.length - 1 ? ', ' : ''}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Registration Button */}
                    {program.registration_link && (
                      <a
                        href={program.registration_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-full inline-flex justify-center items-center px-6 py-3 text-sm font-semibold text-white ${colorScheme.button} rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-30 transform hover:scale-105 shadow-md hover:shadow-lg`}
                      >
                        Register Now
                        <svg className="ml-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Call to Action */}
          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4 text-sm">
              Can't find a suitable program? Want a customized session for your group?
            </p>
            <a 
              href="https://wa.me/919876543210?text=I%20want%20to%20know%20about%20Art%20of%20Living%20programs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-md hover:from-green-700 hover:to-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg transform hover:scale-105 text-sm"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              WhatsApp for Custom Programs
            </a>
          </div>
        </div>
      </section>
    );
  };

  const Navigation = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
      <nav className="bg-white shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-gray-900">Neha Patel</span>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <button
                onClick={() => setCurrentPage('home')}
                className={`${currentPage === 'home' ? 'text-blue-600' : 'text-gray-700'} hover:text-blue-600 px-3 py-2 text-sm font-medium transition`}>
                Home
              </button>
              <button
                onClick={() => setCurrentPage('financial')}
                className={`${currentPage === 'financial' ? 'text-blue-600' : 'text-gray-700'} hover:text-blue-600 px-3 py-2 text-sm font-medium transition`}>
                Financial Planning
              </button>
              <button
                onClick={() => setCurrentPage('yoga')}
                className={`${currentPage === 'yoga' ? 'text-blue-600' : 'text-gray-700'} hover:text-blue-600 px-3 py-2 text-sm font-medium transition`}>
                Yoga & Wellness
              </button>
              <button
                onClick={() => setCurrentPage('aol')}
                className={`${currentPage === 'aol' ? 'text-blue-600' : 'text-gray-700'} hover:text-blue-600 px-3 py-2 text-sm font-medium transition`}>
                Art of Living
              </button>
              <a
                href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27d%20like%20to%20know%20more%20about%20your%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-blue-600 focus:outline-none focus:text-blue-600 p-2">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
                <button
                  onClick={() => {
                    setCurrentPage('home');
                    setMobileMenuOpen(false);
                  }}
                  className={`${currentPage === 'home' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'} hover:text-blue-600 hover:bg-blue-50 block px-3 py-2 text-base font-medium transition w-full text-left`}>
                  Home
                </button>
                <button
                  onClick={() => {
                    setCurrentPage('financial');
                    setMobileMenuOpen(false);
                  }}
                  className={`${currentPage === 'financial' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'} hover:text-blue-600 hover:bg-blue-50 block px-3 py-2 text-base font-medium transition w-full text-left`}>
                  Financial Planning
                </button>
                <button
                  onClick={() => {
                    setCurrentPage('yoga');
                    setMobileMenuOpen(false);
                  }}
                  className={`${currentPage === 'yoga' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'} hover:text-blue-600 hover:bg-blue-50 block px-3 py-2 text-base font-medium transition w-full text-left`}>
                  Yoga & Wellness
                </button>
                <button
                  onClick={() => {
                    setCurrentPage('aol');
                    setMobileMenuOpen(false);
                  }}
                  className={`${currentPage === 'aol' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'} hover:text-blue-600 hover:bg-blue-50 block px-3 py-2 text-base font-medium transition w-full text-left`}>
                  Art of Living
                </button>
                <a
                  href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27d%20like%20to%20know%20more%20about%20your%20services."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 text-base font-medium transition block text-center mx-3 my-2 rounded-lg inline-flex items-center justify-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}>
                  <MessageCircle className="w-4 h-4" />
                  Contact WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>
      </nav>
    );
  };

  const Breadcrumb = ({ currentPage }) => {
    const pageLabels = {
      home: 'Home',
      financial: 'Financial Planning',
      yoga: 'Yoga & Wellness',
      aol: 'Art of Living',
      admin: 'Admin'
    };

    if (currentPage === 'home') return null;

    return (
      <nav className="bg-gray-50 py-3 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <button
                onClick={() => setCurrentPage('home')}
                className="text-blue-600 hover:text-blue-800 transition-colors">
                Home
              </button>
            </li>
            <li className="text-gray-400">/</li>
            <li className="text-gray-900 font-medium">
              {pageLabels[currentPage]}
            </li>
          </ol>
        </div>
      </nav>
    );
  };

  const SocialLinks = () => (
    <div className="flex space-x-4">
      <a href="https://www.linkedin.com/in/np1991" target="_blank" rel="noopener noreferrer" 
         className="text-gray-600 hover:text-blue-600 transition">
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      </a>
      <a href="https://pinterest.com" className="text-gray-600 hover:text-pink-600 transition">
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.174-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.748.099.118.112.221.085.342-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24.009 12.017 24.009c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641.001 12.017.001z"/>
        </svg>
      </a>
      <a href="https://twitter.com" className="text-gray-600 hover:text-gray-900 transition">
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      </a>
    </div>
  );

  const HomePage = () => (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">Hello, I am Neha Patel</h1>
              <p className="text-xl mb-8 text-blue-100">
                Passionate about Personal Finances, Mental Health, and Yoga Teaching
              </p>
              <p className="text-lg mb-8 text-blue-200">
                Bringing clarity to your financial life, peace to your mind, and strength to your body through proven expertise and personalized guidance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={() => setCurrentPage('financial')}
                  className="bg-white text-blue-900 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition">
                  Financial Consultation
                </button>
                <button 
                  onClick={() => setCurrentPage('yoga')}
                  className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition">
                  Explore Wellness
                </button>
              </div>
            </div>
            <div className="relative">
              <div className="w-80 h-80 mx-auto rounded-2xl overflow-hidden shadow-2xl">
                 <img
                   src="/finance2.jpeg"
                   alt="Neha Patel - Certified Financial Planning Expert with 9+ years experience in wealth building and investment strategies"
                   className="w-full h-full object-cover"
                   loading="lazy"
                   width="320"
                   height="320"
                   decoding="async"
                   style={{aspectRatio: '1/1'}}
                 />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">My Expertise</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition border-l-4 border-blue-600">
              <DollarSign className="w-12 h-12 text-blue-600 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Financial Planning</h3>
              <p className="text-gray-600 mb-4">9+ years of experience helping individuals and families achieve financial clarity and build generational wealth.</p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li>• Value-Based Budgeting</li>
                <li>• Automated Investment Strategies</li>
                <li>• Tax Optimization</li>
                <li>• Debt Acceleration</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setCurrentPage('financial')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex-1">
                  Learn More
                </button>
                <a
                  href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20your%20financial%20planning%20services.%20I%27d%20like%20to%20discuss%20my%20financial%20goals%20and%20learn%20about%20your%20approach.%20Can%20we%20schedule%20a%20consultation%3F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex-1 text-center inline-flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition border-l-4 border-purple-600">
              <Heart className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Yoga & Wellness</h3>
              <p className="text-gray-600 mb-4">Certified yoga instructor focused on mental health and physical well-being through mindful practice.</p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li>• Personalized Yoga Sessions</li>
                <li>• Mental Health Support</li>
                <li>• Stress Management</li>
                <li>• Mindfulness Training</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setCurrentPage('yoga')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex-1">
                  Learn More
                </button>
                <a
                  href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20your%20yoga%20and%20wellness%20services.%20I%27d%20like%20to%20know%20more%20about%20your%20mental%20health%20focused%20approach%20and%20available%20class%20options.%20Can%20we%20discuss%3F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex-1 text-center inline-flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition border-l-4 border-green-600">
              <Flower className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Art of Living</h3>
              <p className="text-gray-600 mb-4">Transformative online courses and programs designed to bring happiness and inner peace into your daily life.</p>
              <ul className="text-sm text-gray-600 space-y-2 mb-6">
                <li>• Happiness Programs</li>
                <li>• Breathing Techniques</li>
                <li>• Meditation Courses</li>
                <li>• Stress Relief Programs</li>
              </ul>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => setCurrentPage('aol')}
                  className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex-1">
                  Learn More
                </button>
                <a
                  href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20Art%20of%20Living%20programs.%20I%27d%20like%20to%20know%20more%20about%20the%20Happiness%20Program%2C%20breathing%20techniques%2C%20and%20meditation%20courses.%20Can%20you%20share%20more%20details%3F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition flex-1 text-center inline-flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">What Clients Say</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic">
                "Neha's value-based budgeting approach completely transformed how I think about money. Within 6 months, I had my emergency fund set up and automated my investments. Her guidance gave me the financial confidence I never had before."
              </p>
              <p className="font-semibold">Priya S.</p>
              <p className="text-sm text-gray-500">Financial Planning Client</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic">
                "The mental health focused yoga sessions have been life-changing. Neha's approach to combining traditional poses with mindfulness practices helped me manage my anxiety better than years of other therapies."
              </p>
              <p className="font-semibold">Rajesh M.</p>
              <p className="text-sm text-gray-500">Yoga & Wellness Client</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex mb-4">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-gray-600 mb-4 italic">
                "The Happiness Program was exactly what I needed during a stressful period in my life. The Sudarshan Kriya technique has become my daily anchor, and I feel more centered and peaceful than ever."
              </p>
              <p className="font-semibold">Anita K.</p>
              <p className="text-sm text-gray-500">Art of Living Participant</p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials & Social Proof */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Credentials & Experience</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-white rounded-full p-6 shadow-lg mx-auto w-24 h-24 flex items-center justify-center mb-4">
                <CheckCircle className="w-12 h-12 text-blue-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">9+ Years</h3>
              <p className="text-gray-600 text-sm">Financial Planning Experience</p>
            </div>

            <div className="text-center">
              <div className="bg-white rounded-full p-6 shadow-lg mx-auto w-24 h-24 flex items-center justify-center mb-4">
                <Calendar className="w-12 h-12 text-purple-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Master's Degree</h3>
              <p className="text-gray-600 text-sm">Financial Planning, Mysore University (2015)</p>
            </div>

            <div className="text-center">
              <div className="bg-white rounded-full p-6 shadow-lg mx-auto w-24 h-24 flex items-center justify-center mb-4">
                <Heart className="w-12 h-12 text-pink-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Certified Yoga</h3>
              <p className="text-gray-600 text-sm">Mental Health Focused Instructor</p>
            </div>

            <div className="text-center">
              <div className="bg-white rounded-full p-6 shadow-lg mx-auto w-24 h-24 flex items-center justify-center mb-4">
                <Flower className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Art of Living</h3>
              <p className="text-gray-600 text-sm">Certified Teacher & Practitioner</p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="bg-white p-8 rounded-xl shadow-lg max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Professional Mission</h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                With a Master's in Financial Planning and extensive training in wellness practices, I bridge the gap between financial security and personal well-being.
                My approach combines evidence-based financial strategies with holistic wellness techniques, ensuring clients achieve not just monetary success,
                but also mental clarity and emotional balance.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-6">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">CFP Certified</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium">Yoga Alliance Certified</span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">Art of Living Teacher</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">Mental Health Advocate</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const FinancialPlanningPage = () => (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">Financial Planning Excellence</h1>
              <p className="text-xl mb-6 text-blue-100">
                "General knowledge about money isn't so general anymore."
              </p>
              <p className="text-lg mb-8">
                With a Master's in Financial Planning from Mysore University (2015) and over 9 years of experience, 
                I help you align your money with your life goals through automated, sustainable systems.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%20would%20like%20to%20book%20a%20free%20financial%20consultation.%20I%27m%20interested%20in%20learning%20about%20value-based%20budgeting%2C%20investment%20automation%2C%20and%20building%20long-term%20wealth.%20Please%20let%20me%20know%20your%20availability."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Consultation
                </a>
                <a
                  href="https://forms.google.com/financial-consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-blue-900 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition border-2 border-white inline-flex items-center justify-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Book Online
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-80 h-80 mx-auto rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/finance.jpeg"
                  alt="Professional financial planning consultation - Investment automation, tax optimization, and wealth building strategies"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="320"
                  height="320"
                  decoding="async"
                  style={{aspectRatio: '1/1'}}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">What I Offer</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-blue-50 p-6 rounded-xl">
              <CheckCircle className="w-10 h-10 text-blue-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Install Value-Based Budgets</h3>
              <p className="text-gray-700 mb-4">Say goodbye to restrictive budgeting. Create a spending plan rooted in your personal values, so every rupee moves you closer to the life you want.</p>
              <a
                href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20learning%20about%20value-based%20budgeting.%20Can%20you%20help%20me%20create%20a%20spending%20plan%20that%20aligns%20with%20my%20personal%20values%20and%20financial%20goals%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors">
                <Phone className="w-4 h-4" />
                Ask about Budgeting
              </a>
            </div>
            
            <div className="bg-green-50 p-6 rounded-xl">
              <TrendingUp className="w-10 h-10 text-green-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Automate Investments</h3>
              <p className="text-gray-700 mb-4">Design an effortless, automated investment strategy that grows your wealth consistently while you focus on living your life.</p>
              <a
                href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27d%20like%20to%20learn%20about%20automated%20investment%20strategies.%20Can%20you%20help%20me%20design%20a%20system%20that%20grows%20my%20wealth%20consistently%20without%20constant%20monitoring%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-green-600 hover:text-green-800 font-medium text-sm transition-colors">
                <Phone className="w-4 h-4" />
                Learn Investment Automation
              </a>
            </div>
            
            <div className="bg-purple-50 p-6 rounded-xl">
              <DollarSign className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Maximize Tax Deductions</h3>
              <p className="text-gray-700 mb-4">Leverage every available deduction and exemption so you keep more of what you earn and put it to work for your future.</p>
              <a
                href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%20want%20to%20maximize%20my%20tax%20savings%20and%20deductions.%20Can%20you%20help%20me%20identify%20all%20available%20exemptions%20and%20create%20a%20tax-efficient%20financial%20plan%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium text-sm transition-colors">
                <Phone className="w-4 h-4" />
                Optimize Tax Strategy
              </a>
            </div>
            
            <div className="bg-red-50 p-6 rounded-xl">
              <CheckCircle className="w-10 h-10 text-red-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Accelerate Debt Payoff</h3>
              <p className="text-gray-700 mb-4">Break free from debt faster using smart repayment strategies and redirect cash flow toward wealth creation.</p>
              <a
                href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%20need%20help%20with%20debt%20management%20and%20payoff%20strategies.%20Can%20you%20help%20me%20create%20a%20plan%20to%20become%20debt-free%20faster%20and%20redirect%20money%20toward%20wealth%20building%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-red-600 hover:text-red-800 font-medium text-sm transition-colors">
                <Phone className="w-4 h-4" />
                Get Debt-Free Plan
              </a>
            </div>
            
            <div className="bg-indigo-50 p-6 rounded-xl md:col-span-2 lg:col-span-2">
              <Users className="w-10 h-10 text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Build Generational Wealth</h3>
              <p className="text-gray-700 mb-4">Your legacy starts now. Create a financial blueprint that secures your future and sets up your loved ones for long-term prosperity.</p>
              <a
                href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20building%20generational%20wealth%20for%20my%20family.%20Can%20you%20help%20me%20create%20a%20comprehensive%20financial%20blueprint%20that%20ensures%20long-term%20prosperity%20for%20my%20loved%20ones%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium text-sm transition-colors">
                <Phone className="w-4 h-4" />
                Plan Legacy Wealth
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-8">My Mission</h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                To bring financial clarity to individuals and families, helping them align their money with their life goals. 
                Think of it as removing the chaos from your finances and replacing it with a clear, long-term plan that works for you, not against you. 
                Because financial freedom isn't just about wealth—it's about peace of mind.
              </p>
            </div>
            <div className="relative">
              <div className="w-80 h-64 mx-auto rounded-xl overflow-hidden shadow-lg">
                <img
                  src="/finance2.jpeg"
                  alt="Value-based financial planning approach - Creating sustainable wealth management systems for long-term prosperity"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="320"
                  height="256"
                  decoding="async"
                  style={{aspectRatio: '5/4'}}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const YogaWellnessPage = () => (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-900 to-pink-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl font-bold mb-6">Yoga & Mental Wellness</h1>
              <p className="text-xl mb-6 text-purple-100">
                Nurturing your mind, body, and spirit through the ancient wisdom of yoga
              </p>
              <p className="text-lg mb-8">
                As a certified yoga teacher with a deep focus on mental health, I guide you on a journey 
                towards inner peace, physical strength, and emotional balance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20your%20yoga%20and%20wellness%20services.%20I%27d%20like%20to%20know%20more%20about%20mental%20health%20focused%20yoga%20sessions%20and%20how%20they%20can%20help%20with%20stress%20reduction%20and%20emotional%20balance.%20Can%20we%20discuss%20class%20options%3F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105 shadow-lg">
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Inquiry
                </a>
                <a
                  href="https://forms.google.com/yoga-registration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-purple-900 px-6 py-3 rounded-lg font-semibold hover:bg-purple-50 transition border-2 border-white inline-flex items-center justify-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Register Online
                </a>
              </div>
            </div>
            <div className="relative">
              <div className="w-80 h-80 mx-auto rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/yoga.jpeg"
                  alt="Certified yoga instructor specializing in mental health wellness - Stress reduction through traditional Hatha yoga and mindfulness practices"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="320"
                  height="320"
                  decoding="async"
                  style={{aspectRatio: '1/1'}}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Yoga Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Yoga & Wellness Services</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-purple-50 p-8 rounded-xl">
              <Heart className="w-12 h-12 text-purple-600 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Mental Health Focused Yoga</h3>
              <p className="text-gray-700 mb-4">
                Specialized yoga sessions designed to support mental wellness, reduce anxiety, and promote emotional balance through mindful movement and breathing.
              </p>
              <ul className="text-gray-600 space-y-2 mb-6">
                <li>• Stress reduction techniques</li>
                <li>• Anxiety management through asanas</li>
                <li>• Mindfulness integration</li>
                <li>• Emotional regulation practices</li>
              </ul>
              <a
                href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20mental%20health%20focused%20yoga%20sessions.%20I%27d%20like%20to%20know%20how%20yoga%20can%20help%20me%20with%20stress%20reduction%2C%20anxiety%20management%2C%20and%20emotional%20balance.%20Can%20we%20discuss%20personalized%20sessions%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium transition-colors">
                <Phone className="w-4 h-4" />
                Learn About Mental Health Yoga
              </a>
            </div>

            <div className="bg-pink-50 p-8 rounded-xl">
              <Flower className="w-12 h-12 text-pink-600 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Traditional Hatha Yoga</h3>
              <p className="text-gray-700 mb-4">
                Classical yoga practice focusing on physical postures, breathing techniques, and meditation for overall health and spiritual growth.
              </p>
              <ul className="text-gray-600 space-y-2 mb-6">
                <li>• Traditional asana practice</li>
                <li>• Pranayama (breathing exercises)</li>
                <li>• Meditation guidance</li>
                <li>• Flexibility and strength building</li>
              </ul>
              <a
                href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20traditional%20Hatha%20yoga%20classes.%20I%27d%20like%20to%20learn%20about%20classical%20asanas%2C%20pranayama%2C%20and%20meditation%20practices.%20Can%20you%20tell%20me%20about%20your%20Hatha%20yoga%20sessions%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-pink-600 hover:text-pink-800 font-medium transition-colors">
                <Phone className="w-4 h-4" />
                Join Hatha Yoga Classes
              </a>
            </div>

            <div className="bg-indigo-50 p-8 rounded-xl">
              <Users className="w-12 h-12 text-indigo-600 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Group Classes</h3>
              <p className="text-gray-700 mb-4">
                Join our supportive community classes designed for all levels, fostering connection and shared growth in a peaceful environment.
              </p>
              <ul className="text-gray-600 space-y-2 mb-6">
                <li>• Beginner-friendly sessions</li>
                <li>• Advanced practice groups</li>
                <li>• Community support</li>
                <li>• Affordable group rates</li>
              </ul>
              <a
                href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27d%20like%20to%20join%20your%20group%20yoga%20classes.%20Can%20you%20tell%20me%20about%20the%20current%20schedules%2C%20group%20sizes%2C%20and%20what%20level%20would%20be%20suitable%20for%20me%3F%20I%27m%20looking%20for%20a%20supportive%20community%20environment."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-800 font-medium transition-colors">
                <Phone className="w-4 h-4" />
                Join Group Classes
              </a>
            </div>

            <div className="bg-green-50 p-8 rounded-xl">
              <Calendar className="w-12 h-12 text-green-600 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Private Sessions</h3>
              <p className="text-gray-700 mb-4">
                Personalized one-on-one yoga instruction tailored to your specific needs, goals, and physical capabilities.
              </p>
              <ul className="text-gray-600 space-y-2 mb-6">
                <li>• Customized practice plans</li>
                <li>• Individual attention</li>
                <li>• Flexible scheduling</li>
                <li>• Specific goal targeting</li>
              </ul>
              <a
                href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20private%20yoga%20sessions.%20I%27d%20like%20personalized%20instruction%20tailored%20to%20my%20specific%20needs%20and%20goals.%20Can%20we%20discuss%20availability%2C%20pricing%2C%20and%20what%20a%20customized%20practice%20plan%20would%20look%20like%3F"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-green-600 hover:text-green-800 font-medium transition-colors">
                <Phone className="w-4 h-4" />
                Book Private Session
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Mental Health Philosophy */}
      <section className="py-16 bg-gradient-to-r from-purple-100 to-pink-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8">Holistic Approach to Wellness</h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            True wellness encompasses not just physical fitness, but mental and emotional health. Through yoga practice, 
            we create space for healing, growth, and self-discovery. Every session is designed to nurture your complete well-being, 
            helping you find balance in an increasingly demanding world.
          </p>
        </div>
      </section>
    </div>
  );

  const ArtOfLivingPage = () => {
    const [activeTab, setActiveTab] = useState('physical');

    const benefitsData = {
      physical: {
        title: "Physical Benefits",
        icon: Heart,
        benefits: [
          "Reduced stress hormones (cortisol)",
          "Improved cardiovascular health",
          "Better sleep quality",
          "Enhanced immune system function",
          "Increased energy levels",
          "Pain reduction and management"
        ]
      },
      mental: {
        title: "Mental Benefits", 
        icon: TrendingUp,
        benefits: [
          "Improved focus and concentration",
          "Enhanced memory and learning",
          "Reduced anxiety and depression",
          "Better emotional regulation",
          "Increased mental clarity",
          "Enhanced creativity and intuition"
        ]
      },
      spiritual: {
        title: "Spiritual Benefits",
        icon: Flower,
        benefits: [
          "Deeper sense of inner peace",
          "Greater self-awareness",
          "Enhanced sense of purpose",
          "Improved relationships",
          "Increased compassion and empathy",
          "Connection to inner wisdom"
        ]
      }
    };

    const benefits = [
      { icon: <Users className="w-8 h-8" />, value: "450M+", label: "People Reached Globally" },
      { icon: <Heart className="w-8 h-8" />, value: "180+", label: "Countries" },
      { icon: <TrendingUp className="w-8 h-8" />, value: "87%", label: "Stress Reduction" },
      { icon: <Flower className="w-8 h-8" />, value: "40+", label: "Years of Impact" }
    ];

    return (
      <div>
        {/* Hero Section */}
        <section className="relative min-h-[80vh] flex items-center justify-center text-white overflow-hidden">
          {/* Background Image with WebP support */}
          <picture className="absolute inset-0 w-full h-full">
            <source srcSet="/aol-hero-bg.webp" type="image/webp" />
            <img
              src="/aol-hero-bg.jpg"
              alt="Art of Living programs and meditation practices - Sudarshan Kriya breathing techniques and happiness workshops for inner peace and wellness"
              className="w-full h-full object-cover"
              loading="eager"
              decoding="async"
              width="1920"
              height="1080"
              style={{aspectRatio: '16/9'}}
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/50 to-black/70" />
          
          <div className="relative z-10 w-full flex items-center justify-center">
            <div className="max-w-5xl w-full px-4 py-16 text-center mx-auto">
              <div className="flex items-center justify-center mb-6">
                <Flower className="h-12 w-12 text-white" />
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-8 drop-shadow-lg">
                <span className="text-green-300">Transform Your Life</span>{" "}
                <span className="text-teal-300">with Ancient Wisdom</span>
              </h1>
              
              <p className="text-lg sm:text-2xl lg:text-3xl text-white/90 mb-8 leading-relaxed">
                Join millions worldwide who have discovered inner peace, improved health, 
                and deeper happiness through proven breathing techniques and meditation.
              </p>
              <div className="w-24 h-1.5 bg-teal-300 rounded-full mx-auto"></div>
            </div>
          </div>
        </section>

        {/* About Section - The Happiness Program */}
        <section className="relative py-16 lg:py-24 bg-white overflow-hidden">
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="bg-white/90 p-8 lg:p-10 rounded-2xl shadow-xl">
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                    The Happiness Program
                  </h2>
                  <div className="w-20 h-1 bg-teal-500 rounded-full mb-6"></div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    The flagship Art of Living workshop, the Happiness Program is a transformative 
                    3-day journey that combines powerful breathing techniques, practical wisdom, 
                    and guided meditations.
                  </p>
                  <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                    As a certified Art of Living teacher, Neha guides participants through the renowned 
                    Sudarshan Kriya technique and other practices that have helped millions achieve 
                    better physical health, mental clarity, and emotional wellbeing.
                  </p>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-gray-700">Learn powerful stress-relief techniques</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-gray-700">Experience deep meditation</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-teal-500 rounded-full"></div>
                      <span className="text-gray-700">Build lasting inner peace</span>
                    </li>
                  </ul>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a 
                      href="https://wa.me/919876543210?text=I%20want%20to%20know%20more%20about%20the%20Art%20of%20Living%20Happiness%20Program."
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 
                                text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 
                                hover:scale-105 shadow-lg"
                    >
                      <MessageCircle className="w-5 h-5" />
                      Tell me more
                    </a>
                  </div>
                </div>
              </div>
              <div className="lg:pl-8">
                <div className="bg-gray-50 p-8 rounded-2xl shadow-xl">
                  <div className="bg-gradient-to-br from-green-100 to-teal-100 rounded-2xl p-8">
                    <div className="rounded-lg shadow-lg w-full h-64 overflow-hidden bg-gradient-to-br from-green-200 to-teal-200 flex items-center justify-center">
                      <Flower className="w-32 h-32 text-green-600" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 lg:py-24 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Global Impact & Proven Benefits
              </h2>
              <div className="w-20 h-1 bg-teal-500 rounded-full mx-auto mb-6"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div className="bg-white rounded-full p-6 shadow-lg mx-auto w-24 h-24 flex items-center justify-center mb-4">
                    <div className="text-blue-600">{benefit.icon}</div>
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-2">{benefit.value}</div>
                  <div className="text-gray-600">{benefit.label}</div>
                </div>
              ))}
            </div>

            {/* Benefits Tabs */}
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold mb-4 text-gray-900">Scientifically Proven Benefits</h3>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Art of Living techniques have been researched extensively and shown to provide 
                  measurable improvements across physical, mental, and spiritual dimensions.
                </p>
              </div>
              
              {/* Tab Navigation */}
              <div className="flex justify-center mb-8">
                <div className="bg-white rounded-lg p-1 shadow-sm">
                  {Object.entries(benefitsData).map(([key, data]) => {
                    const IconComponent = data.icon;
                    return (
                      <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        className={`flex items-center px-6 py-3 rounded-md transition-colors ${
                          activeTab === key 
                            ? 'bg-blue-600 text-white' 
                            : 'text-gray-600 hover:text-blue-600'
                        }`}
                      >
                        <IconComponent className="h-5 w-5 mr-2" />
                        {data.title}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Tab Content */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-2xl font-bold mb-6 text-gray-900">
                      {benefitsData[activeTab].title}
                    </h4>
                    <ul className="space-y-4">
                      {benefitsData[activeTab].benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start">
                          <div className="w-3 h-3 bg-green-500 rounded-full mr-4 mt-2 flex-shrink-0"></div>
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h5 className="font-semibold text-gray-900 mb-4">Research Highlights:</h5>
                    <div className="space-y-3 text-sm text-gray-700">
                      <p>• Studies show 87% reduction in stress levels after program completion</p>
                      <p>• Participants report 73% improvement in sleep quality</p>
                      <p>• Significant increase in positive emotions and life satisfaction</p>
                      <p>• Programs practiced in 180+ countries worldwide</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Upcoming Programs */}
        <UpcomingPrograms programs={courses} />

        {/* Online Meditation Workshop Section */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Online Meditation & Breath Workshop
              </h2>
              <div className="w-20 h-1 bg-teal-500 rounded-full mx-auto mb-6"></div>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join from anywhere! Learn basic breathing techniques and meditation practices for daily stress management.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-blue-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  What You'll Learn
                </h3>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  This accessible online workshop introduces you to powerful yet simple breathing techniques 
                  and meditation practices that can be easily integrated into your daily routine.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Perfect for beginners or anyone looking to establish a consistent practice for stress relief, 
                  mental clarity, and overall well-being.
                </p>
                
                <div className="bg-green-50 p-6 rounded-xl mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Workshop Includes:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">Basic breathing techniques for immediate stress relief</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">Guided meditation practice</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">Practical tools for daily life</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">Interactive Q&A session</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-teal-50 p-8 rounded-2xl shadow-xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Workshop Details
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                      <Calendar className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Duration</h4>
                      <p className="text-gray-600 text-sm">1.5 hours of interactive learning</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-1">
                      <Users className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Format</h4>
                      <p className="text-gray-600 text-sm">Live online session via Zoom</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center mt-1">
                      <Heart className="w-4 h-4 text-teal-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Perfect For</h4>
                      <p className="text-gray-600 text-sm">Beginners and busy professionals</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mt-1">
                      <CheckCircle className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">What's Included</h4>
                      <p className="text-gray-600 text-sm">Practice recordings & handouts</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-white/80 rounded-lg">
                  <p className="text-sm text-gray-700 italic text-center">
                    "Just 10 minutes of practice can transform your entire day."
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 text-center">
              <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-8 max-w-4xl mx-auto">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Join the Next Online Workshop
                </h3>
                <p className="text-lg text-gray-700 mb-6">
                  Experience the power of breath and meditation from the comfort of your home. 
                  Perfect for anyone looking to reduce stress and find inner peace.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a 
                    href="https://wa.me/919876543210?text=I%20want%20to%20join%20the%20Online%20Meditation%20and%20Breath%20Workshop."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-500 
                             text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 
                             hover:scale-105 shadow-lg"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Register for Workshop
                  </a>
                  <a 
                    href="mailto:neha.patel@email.com?subject=Online%20Meditation%20Workshop%20Inquiry"
                    className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 
                             text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 
                             hover:scale-105 shadow-lg"
                  >
                    <Mail className="w-5 h-5" />
                    Ask Questions
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // Simple Admin Page Component
  const AdminPage = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [apiClient] = useState(() => createApiClient('nehapatel'));

    const handleBackClick = () => {
      setCurrentPage('home');
    };

    const handleLogin = () => {
      setIsAuthenticated(true);
    };

    const handleLogout = () => {
      apiClient.logout();
      setIsAuthenticated(false);
    };

    // Website configuration for nehapatel styling
    const websiteConfig = {
      title: 'Neha Patel Admin',
      subtitle: 'Manage your Art of Living programs',
      websiteName: 'Neha Patel',
      backgroundClass: 'bg-gray-50',
      titleColorClass: 'text-gray-900',
      buttonClass: 'bg-blue-600 hover:bg-blue-700 text-white',
      backLinkClass: 'text-gray-600 hover:text-gray-800',
      formBgClass: 'bg-blue-50',
      iconBgClass: 'bg-blue-100',
      iconColorClass: 'text-blue-600',
      focusColor: '#2563eb',
      instructionsBgClass: 'bg-green-50',
      demoCredentials: {
        username: 'nehapatel',
        password: 'neha@aol2025'
      },
      demoBgClass: 'bg-blue-50',
      demoBorderClass: 'border-blue-200',
      demoTextClass: 'text-blue-800',
      demoContentClass: 'text-blue-600'
    };

    if (!isAuthenticated) {
      return (
        <MultiWebsiteLogin
          websiteConfig={websiteConfig}
          onLogin={handleLogin}
          onBackToSite={handleBackClick}
          apiClient={apiClient}
          isAuthenticated={isAuthenticated}
        />
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-xl p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-3xl font-bold mb-2 text-gray-900">
                  Art of Living Program Manager
                </h1>
                <p className="text-gray-600">Manage programs for Neha Patel</p>
              </div>
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setCurrentPage('home')} 
                  className="text-gray-600 hover:text-gray-800 transition-colors"
                >
                  ← Back to Website
                </button>
                <button 
                  onClick={handleLogout} 
                  className="text-red-600 hover:text-red-700 transition-colors"
                >
                  Logout
                </button>
              </div>
            </div>

            <div className="grid gap-8">
              <div className="bg-blue-50 p-6 rounded-xl">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">
                  Current Programs
                </h2>
                <div className="grid gap-4">
                  {courses.map((course) => (
                    <div key={course.id} className="bg-white p-4 rounded-lg shadow-sm flex justify-between items-center">
                      <div>
                        <h3 className="font-semibold text-gray-900">{course.title}</h3>
                        <p className="text-sm text-gray-600">{course.duration} • {course.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">{course.schedule}</p>
                        <div className="mt-2">
                          <button className="text-blue-600 hover:text-blue-800 text-sm mr-3">Edit</button>
                          <button className="text-red-600 hover:text-red-800 text-sm">Delete</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-50 p-6 rounded-xl">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">
                  Quick Actions
                </h2>
                <div className="grid md:grid-cols-3 gap-4">
                  <button className="bg-green-600 hover:bg-green-700 text-white p-4 rounded-lg text-center transition-colors">
                    <Calendar className="w-8 h-8 mx-auto mb-2" />
                    Add New Program
                  </button>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-lg text-center transition-colors">
                    <Users className="w-8 h-8 mx-auto mb-2" />
                    Manage Registrations
                  </button>
                  <button className="bg-purple-600 hover:bg-purple-700 text-white p-4 rounded-lg text-center transition-colors">
                    <Mail className="w-8 h-8 mx-auto mb-2" />
                    Send Notifications
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">
                  Integration Status
                </h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-gray-700">Multi-Website API</span>
                    <span className="text-yellow-600 text-sm">Ready for Integration</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-gray-700">Website Key</span>
                    <span className="text-gray-500 text-sm font-mono">nehapatel</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-gray-700">API Endpoint</span>
                    <span className="text-blue-600 text-sm">Ready to Connect</span>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>Next Steps:</strong> Contact system administrator to add "nehapatel" website key to the centralized API system and enable full program management functionality.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ContactSection = () => (
    <section className="py-16 bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold mb-8">Get in Touch</h2>
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <Mail className="w-6 h-6 text-blue-400" />
                <div>
                  <p className="font-semibold">Email</p>
                  <a href="mailto:neha.patel.wellness@gmail.com" className="text-blue-400 hover:text-blue-300 transition">neha.patel.wellness@gmail.com</a>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <Phone className="w-6 h-6 text-blue-400" />
                <div>
                  <p className="font-semibold">Phone</p>
                  <a href="tel:+919876543210" className="text-blue-400 hover:text-blue-300 transition">+91 98765 43210</a>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <MessageCircle className="w-6 h-6 text-green-400" />
                <div>
                  <p className="font-semibold">WhatsApp</p>
                  <a href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27d%20like%20to%20know%20more%20about%20your%20services." className="text-green-400 hover:text-green-300 transition">+91 98765 43210</a>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <MapPin className="w-6 h-6 text-blue-400" />
                <div>
                  <p className="font-semibold">Location</p>
                  <p className="text-gray-300">Mumbai, Maharashtra</p>
                </div>
              </div>
            </div>
            <div className="mt-8">
              <p className="text-lg font-semibold mb-4">Connect with me</p>
              <SocialLinks />
            </div>
          </div>
          
          <div>
            <h3 className="text-2xl font-bold mb-6">Quick Contact</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <a
                  href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27d%20like%20to%20book%20a%20free%20financial%20consultation%20to%20discuss%20my%20financial%20goals%20and%20planning%20needs."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium transition text-center text-sm inline-flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Financial Consultation
                </a>
                <a
                  href="https://forms.google.com/financial-consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg font-medium transition text-center text-sm">
                  📋 Book Online
                </a>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <a
                  href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20yoga%20classes%20and%20would%20like%20to%20know%20about%20your%20mental%20health%20focused%20approach%20and%20available%20sessions."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium transition text-center text-sm inline-flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Yoga Inquiry
                </a>
                <a
                  href="https://forms.google.com/yoga-registration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg font-medium transition text-center text-sm">
                  📋 Register Classes
                </a>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <a
                  href="https://wa.me/919876543210?text=Hi%20Neha%2C%20I%27m%20interested%20in%20Art%20of%20Living%20programs%2C%20especially%20the%20Happiness%20Program%20and%20breathing%20techniques.%20Can%20you%20share%20more%20details%3F"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium transition text-center text-sm inline-flex items-center justify-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  AOL Programs
                </a>
                <a
                  href="https://forms.google.com/aol-registration"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-3 rounded-lg font-medium transition text-center text-sm">
                  📋 Enroll Courses
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-white">
      {currentPage !== 'admin' && <Navigation />}
      {currentPage !== 'admin' && <Breadcrumb currentPage={currentPage} />}

      {currentPage === 'home' && <HomePage />}
      {currentPage === 'financial' && <FinancialPlanningPage />}
      {currentPage === 'yoga' && <YogaWellnessPage />}
      {currentPage === 'aol' && <ArtOfLivingPage />}
      {currentPage === 'admin' && <AdminPage />}

      {currentPage !== 'admin' && <ContactSection />}
    </div>
  );
};

export default NehaPatelWebsite;
